import * as React from "react"
import {
    ColumnDef,
    ColumnFiltersState,
    SortingState,
    VisibilityState,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table"
import {Circle} from "lucide-react"
import {Square} from "lucide-react"
import {useMediaQuery} from "@/hooks/use-media-query"

import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table"

import {CircleAlert} from 'lucide-react';
import {ChevronLeft, ChevronRight, ChevronDown} from "lucide-react"
import {Button} from "@/components/ui/button"
import {Progress} from "@/components/ui/progress"
import {CircleHelp} from "lucide-react"
import {Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "@/components/ui/tooltip"

import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/components/ui/card"
import {cn} from "@/lib/utils"
import {useEffect} from "react"
import axiosInstance from "@/api/axios";
import {FaExclamationCircle} from "react-icons/fa";
import {useTranslation} from "react-i18next"

// Helper function to get tooltip content for each column
const getTooltipContent = (columnHeader: string, t: any) => {
    switch (columnHeader) {
        case t("binColumnHeader"):
            return t("binTooltip")
        case t("locationColumnHeader"):
            return t("locationTooltip")
        case t("fillLevelColumnHeader"):
            return t("fillLevelTooltip")
        case t("streamColumnHeader"):
            return t("streamTooltip")
        default:
            return "Column information"
    }
}

export const getColumns = (t: any): ColumnDef<any>[] => [
    {
        accessorKey: "id",
        header: t("binColumnHeader"),
        cell: ({row}) => (
            <div className="capitalize text-center font-semibold truncate max-w-[120px]" title={row.getValue("id")}>
                {row.getValue("id")}
            </div>
        ),
    },
    {
        accessorKey: "location",
        header: t("locationColumnHeader").substring(0, 4) + ".",
        cell: ({row}) => (
            <div className="capitalize text-center text-ellipsis truncate max-w-[120px]"
                 title={row.getValue("location")}>
                {row.getValue("location")}
            </div>
        ),
    },
    {
        accessorKey: "fullness",
        header: t("fillLevelColumnHeader"),
        cell: ({row}) => {
            const fillLevels = row.original.fillLevels;
            const organic = Math.round(fillLevels.organic);
            const pmd = Math.round(fillLevels.pmd);
            const residual = Math.round(fillLevels.residual);
            const paper = Math.round(fillLevels.paper);

            // Find the maximum fullness value
            const maxFullness = Math.max(organic, pmd, residual, paper);

            return (
                <div className="lowercase text-center truncate" title={maxFullness.toString()}>
                    {maxFullness}
                </div>
            );
        },
    },
    {
        accessorKey: "fillLevels",
        header: t("streamColumnHeader").substring(0, 4).toUpperCase() + ".",
        cell: ({row}) => {
            const fillLevels = row.original.fillLevels;
            const organic = fillLevels.organic;
            const pmd = fillLevels.pmd;
            const residual = fillLevels.residual;
            const paper = fillLevels.paper;

            // Create an array of category objects with their values and associated colors
            const categories = [
                {name: "organic", value: Math.round(organic), color: "green"},
                {name: "pmd", value: Math.round(pmd), color: "yellow"},
                {name: "residual", value: Math.round(residual), color: "red"},
                {name: "paper", value: Math.round(paper), color: "blue"}
            ];

            // Find the category with the highest value
            const highestCategory = categories.reduce(
                (max, category) => category.value > max.value ? category : max,
                categories[0]
            );

            return (
                <div className="text-center flex justify-center"
                     title={`${highestCategory.name}: ${highestCategory.value}%`}>
                    <Square
                        size={16}
                        color={highestCategory.color}
                        fill={highestCategory.color}
                    />
                </div>
            )
        }
    },
]

interface DataTableProps extends React.HTMLAttributes<HTMLDivElement> {
    className?: string;
    title?: string;
}

export function DataTable({className, title, ...props}: DataTableProps) {
    const {t} = useTranslation()
    const [sorting, setSorting] = React.useState<SortingState>([])
    const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([])
    const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({})
    const [data, setData] = React.useState([])
    const [allData, setAllData] = React.useState([]) // Store all data
    const [currentPage, setCurrentPage] = React.useState(0)
    const itemsPerPage = 5
    const [expandedRows, setExpandedRows] = React.useState<Record<string, boolean>>({})
    const isLaptopOrSmaller = useMediaQuery("(max-width: 1024px)")

    useEffect(() => {
        const fetchBins = async () => {
            try {
                const response = await axiosInstance.get("/getlivechartfilllevels")
                if (response.data && response.data.length > 0) {
                    setAllData(response.data) // Store all data
                    // Initial data will be set by the pagination useEffect
                } else {
                    console.log("No fill level data received")
                }
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        };

        fetchBins();
        const intervalId = setInterval(fetchBins, 300000);
        return () => clearInterval(intervalId);
    }, []);

    // Helper function to get maximum fill level for a bin
    const getMaxFillLevel = (bin: any) => {
        if (!bin.fillLevels) return 0;
        const { organic, pmd, residual, paper } = bin.fillLevels;
        return Math.max(organic || 0, pmd || 0, residual || 0, paper || 0);
    };

    // Update displayed data when page changes - WITH SORTING
    useEffect(() => {
        if (allData.length === 0) {
            setData([]);
            return;
        }

        const startIndex = currentPage * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;

        // Sort all data by maximum fill level (descending - most full first)
        const sortedData = [...allData].sort((a, b) => {
            return getMaxFillLevel(b) - getMaxFillLevel(a);
        });

        // Then slice for current page
        setData(sortedData.slice(startIndex, endIndex));
    }, [currentPage, allData]);

    // Set column visibility based on screen size
    useEffect(() => {
        if (isLaptopOrSmaller) {
            setColumnVisibility({
                location: false
            });
        } else {
            setColumnVisibility({});
        }
    }, [isLaptopOrSmaller]);

    const columns = React.useMemo(() => getColumns(t), [t])

    const table = useReactTable({
        data,
        columns,
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        onColumnVisibilityChange: setColumnVisibility,
        state: {
            sorting,
            columnFilters,
            columnVisibility,
        },
    })

    // Toggle row expansion
    const toggleRowExpansion = (rowId: string) => {
        setExpandedRows(prev => ({
            ...prev,
            [rowId]: !prev[rowId]
        }));
    };

    return (
        <div className={cn("w-full", className)} {...props}>
            <h4 className="scroll-m-20 text-xl font-medium tracking-tight p-4">{title || t("totalVisitors")}</h4>
            <Table className="w-full border-collapse">
                <TableHeader>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <TableRow
                            key={headerGroup.id}
                            className="border-b transition-colors hover:bg-muted/50"
                        >
                            {headerGroup.headers.map((header) => (
                                <TableHead
                                    key={header.id}
                                    className={cn(
                                        "h-10 px-2 align-middle font-medium text-muted-foreground text-center truncate whitespace-nowrap text-xs leading-4",
                                        isLaptopOrSmaller && header.id.includes("fillLevels") && "hidden"
                                    )}
                                >
                                    {header.isPlaceholder ? null : (
                                        <TooltipProvider>
                                            <UITooltip>
                                                <TooltipTrigger asChild>
                                                    <span className="cursor-help">
                                                        {flexRender(
                                                            header.column.columnDef.header,
                                                            header.getContext()
                                                        )?.toString?.().toUpperCase() || ''}
                                                    </span>
                                                </TooltipTrigger>
                                                <TooltipContent>
                                                    {getTooltipContent(
                                                        flexRender(
                                                            header.column.columnDef.header,
                                                            header.getContext()
                                                        )?.toString?.() || '', t
                                                    )}
                                                </TooltipContent>
                                            </UITooltip>
                                        </TooltipProvider>
                                    )}
                                </TableHead>
                            ))}
                        </TableRow>
                    ))}
                </TableHeader>
                <TableBody className="[&_tr:last-child]:border-0">
                    {table.getRowModel().rows?.length ? (
                        table.getRowModel().rows.map((row) => (
                            <React.Fragment key={row.id}>
                                <TableRow
                                    className="border-b transition-colors hover:bg-muted/50 cursor-pointer"
                                    onClick={() => toggleRowExpansion(row.id)}
                                >
                                    {row.getVisibleCells().map((cell) => (
                                        <TableCell
                                            key={cell.id}
                                            className="p-2 align-middle text-center overflow-hidden"
                                        >
                                            {flexRender(
                                                cell.column.columnDef.cell,
                                                cell.getContext()
                                            )}
                                        </TableCell>
                                    ))}
                                </TableRow>
                                {expandedRows[row.id] && (
                                    <TableRow className="bg-muted/20">
                                        <TableCell colSpan={columns.length} className="p-2">
                                            <div className="grid grid-cols-1 gap-3 py-2 px-4">
                                                {row.original.fillLevels && (
                                                    <>
                                                        <WasteProgressBar
                                                            name={t("organiclabel")}
                                                            value={Math.round(row.original.fillLevels.organic)}
                                                        />
                                                        <WasteProgressBar
                                                            name={t("pmdlabel")}
                                                            value={Math.round(row.original.fillLevels.pmd)}
                                                        />
                                                        <WasteProgressBar
                                                            name={t("residuallabel")}
                                                            value={Math.round(row.original.fillLevels.residual)}
                                                        />
                                                        <WasteProgressBar
                                                            name={t("paperlabel")}
                                                            value={Math.round(row.original.fillLevels.paper)}
                                                        />
                                                    </>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </React.Fragment>
                        ))
                    ) : (
                        <TableRow>
                            <TableCell
                                colSpan={columns.length}
                                className="h-24 text-center"
                            >
                                {t("noResults")}
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
            {allData.length > itemsPerPage && (
                <div className="flex items-center justify-center space-x-2 mt-4 py-4">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                        disabled={currentPage === 0}
                        aria-label={t("previousPage")}
                        className="h-8 w-8 p-0"
                    >
                        <ChevronLeft className="h-4 w-4"/>
                    </Button>

                    <span className="text-sm font-medium px-3">
                        {currentPage + 1} of {Math.ceil(allData.length / itemsPerPage)}
                    </span>

                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(prev => Math.min(Math.ceil(allData.length / itemsPerPage) - 1, prev + 1))}
                        disabled={currentPage >= Math.ceil(allData.length / itemsPerPage) - 1}
                        aria-label={t("nextPage")}
                        className="h-8 w-8 p-0"
                    >
                        <ChevronRight className="h-4 w-4"/>
                    </Button>
                </div>
            )}
        </div>
    )
}

const renderAlertIcon = (name: string, value: number, t: any) => {
    const threshold = 80;
    return value > threshold ? (
        <TooltipProvider>
            <UITooltip>
                <TooltipTrigger asChild>
                    <CircleAlert className="text-red-500 ml-1 h-4 w-4 cursor-help"/>
                </TooltipTrigger>
                <TooltipContent>
                    <p>{t("wasteStreamFull", { name })}</p>
                </TooltipContent>
            </UITooltip>
        </TooltipProvider>
    ) : null;
};

// Component for waste stream progress bar
function WasteProgressBar({name, value}: { name: string; value: number }) {
    const {t} = useTranslation()
    
    return (
        <div className="flex items-start space-x-4">
            <div className="w-24 text-sm font-medium mt-4">{name}:</div>
            <div className="flex-1">
                <div className="relative mb-1">
                    <span className="absolute top-0 left-1/2 -translate-x-1/2 text-xs font-medium text-green-600  ">
                        {value}%
                    </span>
                </div>
                <div className="flex items-center space-x-2 mt-4">
                    <Progress
                        value={value}
                        className="h-2.5"
                    />
                    {renderAlertIcon(name, value, t)}
                </div>
            </div>
        </div>
    );
}
